import React, { useState } from 'react';
import { FiClock, FiPhone, FiFileText, FiAlertCircle, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { api } from '@crm/shared';
import { C } from '../shared/tokens';
import { Sh, Kpi, Err, Empty } from '../shared/ui';
import { exportCsv, exportExcel, ExportButton } from '../shared/ExportButton';
import { MisNotSearched } from '../shared/MisPeriodBar';

export default function UnderProcessMIS({ filters = {} }) {
  const { selUser, users = [] } = filters;

  const [allRows,  setAllRows]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [searched, setSearched] = useState(false);
  const [search,   setSearch]   = useState('');
  const [exporting,setExporting]= useState(false);

  const today    = new Date().toISOString().split('T')[0];

  // No user filter - show all
  const rows = allRows;

  const fuCount = rows.filter(r => r.current_stage === 'FOLLOW_UP').length;
  const loginCnt= rows.filter(r => r.current_stage === 'LOGIN').length;
  const overdue = rows.filter(r => r.next_follow_up_date && r.next_follow_up_date < today).length;

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.customer_name||'').toLowerCase().includes(q)
      || (r.lead_no||'').toLowerCase().includes(q)
      || (r.executive||'').toLowerCase().includes(q);
  });

  const load = async () => {
    setLoading(true); setError(null); setSearched(true);
    try {
      const res = await api.getMisUnderProcess();
      const d = res?.data;
      setAllRows(Array.isArray(d?.results) ? d.results : []);
    } catch (e) { setError(e?.detail || e?.message || 'Failed to load Under Process MIS'); }
    finally { setLoading(false); }
  };

  const COLS = ['Lead No','Customer','Mobile','Product','Stage','Next Follow-up','Executive','Location','Lead Date'];
  const toRow = r => [r.lead_no||`#${r.id}`, r.customer_name||'', r.mobile_number||'', r.product||'', r.current_stage||'', r.next_follow_up_date||'', r.executive||'', r.location||'', r.lead_date||''];

  const handleExport = fmt => {
    setExporting(true);
    const data = filtered.map(toRow);
    if (fmt === 'excel') exportExcel(COLS, data, 'under_process_mis');
    else exportCsv(COLS, data, 'under_process_mis');
    setExporting(false);
  };

  return (
    <>
      <div style={{ background:C.goldL, border:`1px solid #F0D798`, borderRadius:10,
        padding:'10px 16px', display:'flex', alignItems:'flex-start', gap:9, marginBottom:14 }}>
        <FiClock size={13} style={{ color:C.gold, marginTop:1, flexShrink:0 }}/>
        <span style={{ fontSize:12.5, color:'#92400E', fontWeight:500, lineHeight:1.55 }}>
          <strong>Under Process MIS:</strong> All leads currently in Follow-Up or Login stage. Real-time snapshot — no date filter required.
        </span>
      </div>

      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'14px 18px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <button onClick={load} disabled={loading}
          style={{ height:36, padding:'0 20px', borderRadius:8, background:loading?C.navyM:C.navy, color:'#fff', border:'none', fontSize:13, fontWeight:700, cursor:loading?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
          {loading
            ? <><div style={{ width:11, height:11, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'spin 0.8s linear infinite' }}/> Loading…</>
            : <><FiRefreshCw size={13}/> Load / Refresh</>}
        </button>

        {allRows.length > 0 && (
          <>
            <div style={{ position:'relative' }}>
              <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
              <input type="text" placeholder="Search name / lead no…" value={search} onChange={e => setSearch(e.target.value)}
                style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px 0 28px', fontSize:12.5, color:C.t1, background:C.bg, outline:'none', fontFamily:'inherit', width:200 }}
                onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.border}/>
            </div>
            <ExportButton onExport={handleExport} loading={exporting}/>
          </>
        )}
      </div>

      {!searched && !loading && <MisNotSearched label="Click Load / Refresh to view Under Process leads" icon={<FiClock size={22} style={{ color:C.gold, opacity:0.6 }}/>}/>}

      {loading && <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}><div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[0,1,2,3,4].map(i => <Sh key={i} h={48}/>)}</div></div>}

      {!loading && error && <Err msg={error} onRetry={load}/>}

      {searched && !loading && !error && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label="Total Under Process" value={rows.length}  icon={<FiClock size={15}/>}       iconBg={C.goldL}  iconColor={C.gold}   accent={C.goldB}  loading={false}/>
            <Kpi label="Follow-Up"           value={fuCount}      icon={<FiPhone size={15}/>}        iconBg={C.goldL}  iconColor={C.gold}   accent={C.goldB}  loading={false}/>
            <Kpi label="Login Stage"         value={loginCnt}     icon={<FiFileText size={15}/>}     iconBg={C.purpleL} iconColor={C.purpleB} accent={C.purpleB} loading={false}/>
            <Kpi label="Overdue Follow-ups"  value={overdue}      icon={<FiAlertCircle size={15}/>}  iconBg={C.redL}   iconColor={C.redB}   accent={C.redB}   loading={false} sub={overdue > 0 ? 'Needs immediate action' : undefined}/>
          </div>

          {filtered.length === 0 ? <Empty label="No leads found"/> : (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ padding:'11px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', display:'flex', alignItems:'center', gap:8 }}>
                <FiClock size={14} style={{ color:C.gold }}/>
                <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Under Process — Live Snapshot</span>
                <span style={{ background:C.goldL, color:'#92400E', padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:700 }}>{filtered.length} leads</span>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:780 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Lead No','Customer','Mobile','Product','Stage','Next Follow-up','Executive','Location'].map(h => (
                        <th key={h} style={{ padding:'9px 13px', fontSize:10.5, fontWeight:800, color:C.t2, textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap', textAlign:'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => {
                      const isLast   = idx === filtered.length - 1;
                      const isOverdue= r.next_follow_up_date && r.next_follow_up_date < today;
                      const isLogin  = r.current_stage === 'LOGIN';
                      return (
                        <tr key={r.id || idx} style={{ background:idx%2===0?C.white:'#FAFBFC' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#FFFBEB'}
                          onMouseLeave={e => e.currentTarget.style.background = idx%2===0?C.white:'#FAFBFC'}>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4 }}>{idx+1}</td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><span style={{ fontSize:12, fontWeight:700, color:C.navy, fontFamily:'monospace' }}>{r.lead_no||`#${r.id}`}</span></td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:600, color:C.t1 }}>{r.customer_name||'—'}</td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>{r.mobile_number||'—'}</td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{r.product||'—'}</td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, background:isLogin?C.purpleL:C.goldL, color:isLogin?C.purpleB:'#92400E' }}>
                              {r.current_stage === 'FOLLOW_UP' ? 'Follow-Up' : r.current_stage}
                            </span>
                          </td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:isOverdue?700:400, color:isOverdue?C.redB:C.t2 }}>
                            {r.next_follow_up_date
                              ? <>{r.next_follow_up_date}{isOverdue && <span style={{ fontSize:10, color:C.redB, marginLeft:4, fontWeight:800 }}>OVERDUE</span>}</>
                              : '—'}
                          </td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:600, color:C.t1 }}>{r.executive||'—'}</td>
                          <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>{r.location||'—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
